/**
 * FACTS: what every screen needs to know about a mission that is not a number - how it ends, whether it
 * starts with a drop, what rides in a pod, what the job cannot be done without (03 §2, 10 §2). One row per
 * archetype, so the briefing, the loadout screen, the cards, the Daily Drop and the modifier table all read
 * the same truth instead of each asking `if (archetype === ...)`. Pure data; `archetype(id).facts` exposes it.
 */

import type { ArchetypeId } from '../../sim/types';

/** What kind of job it is (10 §2). The 1987 twelve are sorted into the same five. */
export const CATEGORIES = ['demolition', 'intelligence', 'people', 'sabotage', 'endurance'] as const;
export type MissionCategory = (typeof CATEGORIES)[number];

/** The design's "quiet rule". `strict` = knife only until a line is crossed: kept off a theatre's loud chain. */
export type QuietRule = 'none' | 'soft' | 'local' | 'strict' | 'inverted' | 'n/a';

/**
 * How the mission ends. `normal`: Recall. `relocates`: Recall, and the pickup moves to the capture (4).
 * `at-clock-zero`: no Recall, the Osprey comes when the clock runs out (11). `on-completion`: no Recall,
 * the Osprey comes on its own when the job is done (36).
 */
export type RecallMode = 'normal' | 'relocates' | 'at-clock-zero' | 'on-completion';

/**
 * What a stated time on the ground clock means here. `deadline`: the mission is lost if the job is not done
 * by then (the train leaves). `event`: something arrives then, and must be met or beaten (the courier, the
 * search party). `bonus`: it can only help (13's convoy). The HUD counts all three down from the start.
 */
export type ClockRule = 'none' | 'bonus' | 'event' | 'deadline';

/** The three things that explode, as the loadout counts them. */
export type KitItem = 'grenades' | 'laws' | 'bombs';

/** "Objective requires": `count` of any of `items`. `atLeast`: the real number varies with the map, never less. */
export interface KitNeed {
  items: readonly KitItem[];
  count: number;
  atLeast?: true;
  /** What they are for, upper case, as the loadout prints it: "ONE FOR EACH PIER". */
  use?: string;
}

/** Something mission-critical that rides in a supply pod (mirrors the sim's `QuestItemDef`). */
export interface QuestItemFact {
  name: string;
  /** 0-2 = pod A-C. */
  pod: 0 | 1 | 2;
  /** Carry units once it is on his back. */
  weight: number;
}

export interface ArchetypeFacts {
  category: MissionCategory;
  quiet: QuietRule;
  recall: RecallMode;
  clock: ClockRule;
  /** No insertion: he starts on the ground, a prisoner, and only what is in the pods is his (32). */
  startCaptured: boolean;
  questItem: QuestItemFact | null;
  /** The job needs light to see by: never a Night Op (23). */
  daylight: boolean;
  /** Bunkers on this ground: the loadout screen suggests a LAW (02 §9.2). */
  bunkers: boolean;
  /**
   * A stolen uniform (02 §6.4) is the way this job is MEANT to be done, as the briefing says out loud
   * ("AN ENEMY UNIFORM FROM THE GUARDHOUSE IS THE INTENDED WAY IN") and as the generator guarantees by
   * planting a donor at a guard house. Only these missions carry the uniform standing order: nearly every
   * map has a hut somewhere, and a line on all of them is noise on the thirty that do not need it.
   */
  uniformRoute: boolean;
  needs: readonly KitNeed[];
}

const BOMB: KitNeed = { items: ['bombs'], count: 1 };
const LAW_OR_BOMB: KitNeed = { items: ['laws', 'bombs'], count: 1 };
const ANY_EXPLOSIVE: KitNeed = { items: ['grenades', 'laws', 'bombs'], count: 1 };

const PLAIN: ArchetypeFacts = {
  category: 'demolition', quiet: 'none', recall: 'normal', clock: 'none', startCaptured: false, questItem: null,
  daylight: false, bunkers: false, uniformRoute: false, needs: [],
};

const fact = (category: MissionCategory, over: Partial<ArchetypeFacts> = {}): ArchetypeFacts =>
  ({ ...PLAIN, category, ...over });

export const FACTS: Readonly<Record<ArchetypeId, ArchetypeFacts>> = {
  // The 1987 twelve (03 §2).
  'munitions-depot': fact('demolition', { bunkers: true, needs: [LAW_OR_BOMB] }),
  'code-book': fact('intelligence'),
  'enemy-aircraft': fact('demolition', { quiet: 'strict', needs: [ANY_EXPLOSIVE] }),
  'capture-officer': fact('people', { quiet: 'soft', recall: 'relocates' }),
  pipeline: fact('demolition', { needs: [BOMB] }),
  'radar-array': fact('demolition', { needs: [ANY_EXPLOSIVE] }),
  'sam-site': fact('demolition', { quiet: 'strict', bunkers: true, needs: [ANY_EXPLOSIVE] }),
  'pow-camp': fact('people', { quiet: 'strict', needs: [ANY_EXPLOSIVE] }),
  // 9: "AN ENEMY UNIFORM FROM THE GUARDHOUSE IS THE INTENDED WAY IN" (briefing/orders.ts).
  'photograph-aircraft': fact('intelligence', { quiet: 'local', uniformRoute: true }),
  hostages: fact('people', { quiet: 'soft', needs: [ANY_EXPLOSIVE] }),
  diversion: fact('endurance', { quiet: 'inverted', recall: 'at-clock-zero', bunkers: true }),
  // 12: "A GUARD CAUGHT INSIDE A GUARDHOUSE GIVES YOU A UNIFORM. THAT IS THE INTENDED WAY IN."
  'delayed-sabotage': fact('sabotage', { quiet: 'local', uniformRoute: true, needs: [BOMB] }),

  // Demolition (10 §2, 13-20).
  'blow-bridge': fact('demolition', {
    clock: 'bonus', needs: [{ items: ['bombs'], count: 2, atLeast: true, use: 'ONE FOR EACH PIER' }],
  }),
  'supply-train': fact('demolition', {
    quiet: 'soft', clock: 'deadline', needs: [{ ...LAW_OR_BOMB, use: 'FOR THE LOCOMOTIVE' }],
  }),
  'artillery-battery': fact('demolition', {
    needs: [{ items: ['laws', 'bombs'], count: 3, atLeast: true, use: 'ONE FOR EACH GUN' }],
  }),
  'relay-mast': fact('demolition', { needs: [{ ...BOMB, use: 'FOR THE MAST' }] }),
  'missile-convoy': fact('demolition', {
    quiet: 'strict', needs: [{ items: ['laws', 'bombs'], count: 2, atLeast: true, use: 'ONE FOR EACH LAUNCHER' }],
  }),
  'fuel-depot': fact('demolition', { needs: [ANY_EXPLOSIVE] }),
  'coastal-guns': fact('demolition', {
    bunkers: true, needs: [{ items: ['bombs'], count: 2, use: 'ONE FOR EACH CASEMATE' }],
  }),
  'ammo-barges': fact('demolition', {
    quiet: 'soft', needs: [{ items: ['bombs'], count: 3, use: 'ONE FOR EACH BARGE' }],
  }),

  // Intelligence (21-26). 21 and 22 are built round the guard-house donor: `uniformDonor` is a
  // generator check on both (src/gen/archetypes/{stealPlans,wiretap}.ts), so the route is always there.
  'steal-plans': fact('intelligence', { quiet: 'strict', uniformRoute: true }),
  wiretap: fact('intelligence', { quiet: 'local', uniformRoute: true }),
  'photograph-convoy': fact('intelligence', { quiet: 'local', clock: 'deadline', daylight: true }),
  'black-box': fact('intelligence', { clock: 'event' }),
  'forward-observer': fact('intelligence', { quiet: 'soft' }),
  'courier-ambush': fact('intelligence', { quiet: 'strict', clock: 'event' }),

  // People (27-32).
  'downed-pilot': fact('people', { clock: 'deadline' }),
  defector: fact('people', { quiet: 'soft', clock: 'event' }),
  'eliminate-general': fact('people', { quiet: 'strict' }),
  'snatch-scientist': fact('people', { quiet: 'soft', uniformRoute: true }),
  'partisan-rendezvous': fact('people', { questItem: { name: 'RADIO SET', pod: 0, weight: 8 } }),
  'prison-break': fact('people', { quiet: 'n/a', clock: 'event', startCaptured: true }),

  // Sabotage (33-35) and endurance (36).
  'power-station': fact('sabotage', { quiet: 'soft' }),
  // Its generator says the same as 21, 22 and 30 - and enforces it with the same uniformDonor check.
  'mine-road': fact('sabotage', {
    quiet: 'local', clock: 'deadline', uniformRoute: true,
    needs: [{ items: ['bombs'], count: 3, atLeast: true, use: 'ONE FOR EACH CULVERT' }],
  }),
  beacon: fact('sabotage', { quiet: 'strict', questItem: { name: 'STRIKE BEACON', pod: 0, weight: 4 } }),
  'hold-crossing': fact('endurance', { recall: 'on-completion', clock: 'event' }),
};

/** No early Recall: the player cannot abort (11, 36). */
export const recallDisabled = (facts: ArchetypeFacts): boolean =>
  facts.recall === 'at-clock-zero' || facts.recall === 'on-completion';

/** The small tags a mission card carries, in the order they are shown. */
export const MISSION_TAGS = ['timed', 'no-drop', 'pod', 'no-recall', 'quiet'] as const;
export type MissionTag = (typeof MISSION_TAGS)[number];

export function missionTags(facts: ArchetypeFacts): MissionTag[] {
  const on: Record<MissionTag, boolean> = {
    timed: facts.clock === 'event' || facts.clock === 'deadline',
    'no-drop': facts.startCaptured,
    pod: facts.questItem !== null,
    'no-recall': recallDisabled(facts),
    quiet: facts.quiet === 'strict',
  };
  return MISSION_TAGS.filter((tag) => on[tag]);
}

export const POD_LETTERS = 'ABC';

/** What the tag says on a card; the pod tag names its pod ("POD A"). */
export function tagLabel(tag: MissionTag, facts: ArchetypeFacts): string {
  if (tag === 'pod') return `POD ${POD_LETTERS[facts.questItem?.pod ?? 0] ?? 'A'}`;
  return TAG_TEXT[tag].name;
}

/** Names and one-line meanings, for `describe('tag:<id>')` and the cards' tooltips. */
export const TAG_TEXT: Readonly<Record<MissionTag, { name: string; text: string }>> = {
  timed: { name: 'TIMED', text: 'Something happens at a stated time. The countdown is on the HUD from the start.' },
  'no-drop': {
    name: 'NO DROP', text: 'No insertion: he starts on the ground, a prisoner with a knife. Only the pods are his.',
  },
  pod: {
    name: 'POD A',
    text: 'A mission item rides in this pod. Leave it aboard or smash it and the mission is lost at the drop.',
  },
  'no-recall': { name: 'NO RECALL', text: 'Recall is disabled: the Osprey comes on its own, and there is no abort.' },
  quiet: {
    name: 'QUIET',
    text: 'Knife only until the line the orders name is crossed. The suppressed pistol breaks no silence.',
  },
};

export const CATEGORY_TEXT: Readonly<Record<MissionCategory, { name: string; text: string }>> = {
  demolition: { name: 'Demolition', text: 'Something has to be destroyed. Pack what destroys it.' },
  intelligence: { name: 'Intelligence', text: 'Something has to be taken, seen or marked - and brought home.' },
  people: { name: 'People', text: 'The mission is a person: to bring out, to meet, or to kill.' },
  sabotage: { name: 'Sabotage', text: 'Quiet work that does its damage after you have gone.' },
  endurance: { name: 'Endurance', text: 'No early Recall. Stay alive until the Osprey comes on its own.' },
};
